import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Check, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, PageHeader } from "@/components/common/Primitives";
import { StatusBadge } from "@/components/common/StatusBadge";

const loadScript = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true);
  s.onerror = () => res(false);
  document.body.appendChild(s);
});

export default function Subscription() {
  const { user, restaurant } = useAuth();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  const load = async () => {
    const [s, p] = await Promise.all([api.get("/subscription"), api.get("/plans")]);
    setData(s.data);
    setPlans(p.data.items.filter((x) => x.price > 0));
  };
  useEffect(() => { load(); }, []);

  const buy = async (plan) => {
    setBusy(plan.id);
    try {
      const { data: o } = await api.post("/billing/checkout", { plan_id: plan.id });
      if (!(await loadScript())) throw new Error("Could not load Razorpay checkout");
      const rzp = new window.Razorpay({
        key: o.key_id, order_id: o.order_id, amount: o.amount, currency: o.currency,
        name: "Smart Menu", description: o.plan_name,
        prefill: { email: user.email, name: user.name, contact: restaurant?.phone },
        theme: { color: "#10b981" },
        handler: async (resp) => {
          try {
            await api.post("/billing/verify", resp);
            toast.success("Payment verified — subscription active");
            load();
          } catch (e) { toast.error(apiError(e, "Verification failed")); }
        },
        modal: { ondismiss: () => setBusy("") },
      });
      rzp.on("payment.failed", () => toast.error("Payment failed. You have not been charged."));
      rzp.open();
    } catch (e) {
      toast.error(apiError(e, e.message));
    } finally { setBusy(""); }
  };

  if (!data) return <Loader />;
  const sub = data.subscription;
  return (
    <div>
      <PageHeader title="Subscription" subtitle="Your Smart Menu plan and billing history." />
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="current-subscription">
        {sub ? (
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <p className="text-sm text-slate-500">Current plan</p>
              <p className="font-heading text-2xl font-bold text-slate-900">{sub.plan_name}</p>
            </div>
            <StatusBadge status={sub.effective_status} />
            <p className="text-sm text-slate-500">Valid till <b>{format(new Date(sub.expiry_date), "dd MMM yyyy")}</b> (+{sub.grace_days || 0} day grace)</p>
          </div>
        ) : <p className="text-sm text-slate-500">Your free trial starts once the Super Admin approves your restaurant.</p>}
      </div>

      {!data.payments_enabled && (
        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="payments-disabled-notice">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Online payments are not enabled yet. Contact the platform administrator to activate or renew your plan.
        </div>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {plans.map((p) => (
          <div key={p.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6" data-testid={`plan-card-${p.id}`}>
            <p className="font-heading text-lg font-semibold text-slate-900">{p.name}</p>
            <p className="mt-1"><span className="font-heading text-3xl font-bold">{money(p.price)}</span><span className="text-sm text-slate-400"> / {p.duration_days} days</span></p>
            <ul className="mt-4 flex-1 space-y-2 text-sm text-slate-600">
              {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-emerald-500" />{f}</li>)}
            </ul>
            <Button className="mt-5 bg-emerald-500 hover:bg-emerald-600" disabled={!data.payments_enabled || !!busy} onClick={() => buy(p)} data-testid={`plan-buy-${p.id}`}>
              {busy === p.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{sub?.plan_id === p.id ? "Renew" : "Choose plan"}
            </Button>
          </div>
        ))}
      </div>

      <h2 className="mb-3 mt-10 font-heading text-lg font-semibold text-slate-900">Billing history</h2>
      {data.payments.length === 0 ? <p className="text-sm text-slate-400" data-testid="billing-empty">No payments yet.</p> : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="billing-table">
            <thead><tr className="text-left text-xs uppercase text-slate-400"><th className="p-3">Date</th><th className="p-3">Plan</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Payment ID</th></tr></thead>
            <tbody>
              {data.payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="p-3">{format(new Date(p.created_at), "dd MMM yyyy")}</td><td className="p-3">{p.plan_name}</td>
                  <td className="p-3">{money(p.amount)}</td><td className="p-3"><StatusBadge status={p.status} /></td>
                  <td className="p-3 font-mono text-xs text-slate-500">{p.razorpay_payment_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
